// frontend/src/routes/AppRoutes.js
// Rutas principales de la aplicación - USANDO PÁGINAS EXISTENTES
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// Versión mínima para aislar error: sólo login + layout + formulario equipos
import Layout from '../components/layout/Layout.jsx';
import Login from '../pages/Login.jsx';
import ProtectedRoute from '../components/ProtectedRoute.js';
import { useAuth } from '../hooks/useAuth';
import { isPlatformStandaloneMode } from '../utils/runtimeMode';
import { inicioDePortal } from '../utils/portales';

// Páginas principales
import Dashboard from '../pages/Dashboard.jsx';
import ListaEquipos from '../pages/ListaEquipos.jsx';
import ListaReparaciones from '../pages/ListaReparaciones.jsx';
import ListaPartesDiarios from '../pages/ListaPartesDiarios.jsx';
import ListaLubricantes from '../pages/ListaLubricantes.jsx';
import ListaDocumentacion from '../pages/ListaDocumentacion.jsx';
// import ListaPlanesMantenimiento from '../pages/ListaPlanesMantenimiento.jsx';

// Formularios

// Changelog

// Cambio de contraseña obligatorio (admin inicial con credencial por defecto)
import CambioPasswordObligatorio from '../pages/CambioPasswordObligatorio.jsx';

// Aterrizaje del login unificado desde la landing (canje del código de acceso)
import AccesoHandoff from '../pages/AccesoHandoff.jsx';

// Portal mecánico

// Portal de despacho de combustible

// Portal QR e impresión QR: lazy (declarados arriba junto a los demás lazy)

// Configuración
import {
  ConfiguracionMarcas,
  ConfiguracionModelos,
  ConfiguracionFamilias,
  ConfiguracionTiposEquipo,
  ConfiguracionEstadosEquipo,
  ConfiguracionSucursales,
  ConfiguracionCentrosCosto,

  ConfiguracionMecanicos,
  ConfiguracionMotivosDetencion,
  ConfiguracionEstadosReparacion,
  ConfiguracionTiposContador,
  ConfiguracionTareasEngrase,
  ConfiguracionTiposDocumentacion,
  ConfiguracionObras,
  ConfiguracionSectoresTrabajo,
  ConfiguracionSistemas,
  ConfiguracionComponentes,
  ConfiguracionDepositos,
  ConfiguracionTareas,
  ConfiguracionTiposRutina,
  ConfiguracionFamiliasRepuesto,
  ConfiguracionSubfamiliasRepuesto,
  ConfiguracionMarcasRepuesto,
  ConfiguracionUnidadesMedida,
  ConfiguracionRepuestos,
  ConfiguracionEmpresa,
  ConfiguracionEmpresas,
  ConfiguracionCheckList,
  ConfiguracionPuntosControl,
  ConfiguracionCategoriasLogistica,
  ConfiguracionCategoriasCargas,
  ConfiguracionCentrosLogisticos,
  ConfiguracionTransportistas,
  ConfiguracionProveedores,
  ConfiguracionClientesAlquiler,
  ConfiguracionUbicacionesLogisticas,
  ConfiguracionFlotaTransporte,
  ConfiguracionConectoresGPS,
  ConfiguracionMapeoGPS,
  ConfiguracionLugaresDespacho,
  ConfiguracionTiposCombustible,
} from '../pages/configuraciones';

// Lazy: la página de informes carga la librería de gráficos solo al entrar al módulo
const InformesPage = React.lazy(() => import('../pages/InformesPage.jsx'));

// Lazy: módulos pesados fuera del bundle inicial (mapas de Google, QR, almacén).
// Cada uno baja recién al entrar a su pantalla, igual que InformesPage.
const ListaLogistica = React.lazy(() => import('../pages/ListaLogistica.jsx'));
const PaginaLogistica = React.lazy(() => import('../pages/PaginaLogistica.jsx'));
const ListaRental = React.lazy(() => import('../pages/ListaRental.jsx'));
const PaginaRental = React.lazy(() => import('../pages/PaginaRental.jsx'));
const AlmacenPage = React.lazy(() => import('../pages/AlmacenPage.jsx'));
const PortalQRPage = React.lazy(() => import('../pages/PortalQR/PortalQRPage.jsx'));
const PortalQRParteDiario = React.lazy(() => import('../pages/PortalQR/PortalQRParteDiario.jsx'));
const PortalQRDocumentos = React.lazy(() => import('../pages/PortalQR/PortalQRDocumentos.jsx'));
const PortalQRReparaciones = React.lazy(() => import('../pages/PortalQR/PortalQRReparaciones.jsx'));
const QrImprimir = React.lazy(() => import('../pages/QrImprimir/QrImprimir.jsx'));

// Rutas fuera del arranque: cada una baja su código recién al entrar.
const ListaMantenimientos = React.lazy(() => import('../pages/Mantenimiento/ListaMantenimientos.jsx'));
const RutinasMantenimiento = React.lazy(() => import('../pages/Mantenimiento/RutinasMantenimientoList.jsx'));
const TareasMantenimiento = React.lazy(() => import('../pages/Mantenimiento/TareasMantenimiento.jsx'));
const PlanesMantenimiento = React.lazy(() => import('../pages/Mantenimiento/PlanesMantenimiento.jsx'));
const PlanForm = React.lazy(() => import('../pages/Mantenimiento/PlanForm.jsx'));
const RutinaMantenimiento = React.lazy(() => import('../pages/Mantenimiento/RutinaMantenimiento.jsx'));
const PlanillaMantenimiento = React.lazy(() => import('../pages/Mantenimiento/PlanillaMantenimiento.jsx'));
const GrillaPlanMantenimiento = React.lazy(() => import('../pages/Mantenimiento/GrillaPlanMantenimiento.jsx'));
const PerfilEquipo = React.lazy(() => import('../pages/PerfilEquipo.jsx'));
const FlotaGrilla = React.lazy(() => import('../pages/FlotaGrilla.jsx'));
const PaginaOTC = React.lazy(() => import('../pages/PaginaOTC.jsx'));
const CheckListFormulario = React.lazy(() => import('../pages/CheckListFormulario.jsx'));
const CheckListImpresion = React.lazy(() => import('../pages/CheckListImpresion.jsx'));
const FormularioEquipo = React.lazy(() => import('../components/formularios/FormularioEquipo.jsx'));
const Changelog = React.lazy(() => import('../pages/Changelog.jsx'));
const PortalMecanicoDashboard = React.lazy(() => import('../pages/PortalMecanico/PortalMecanicoDashboard.jsx'));
const PortalDespachoDashboard = React.lazy(() => import('../pages/PortalDespacho/PortalDespachoDashboard.jsx'));
const ListaDespachosCombustible = React.lazy(() => import('../pages/ListaDespachosCombustible.jsx'));
const ConfiguracionPage = React.lazy(() => import('../pages/ConfiguracionPage.jsx'));
const ConfiguracionUsuarios = React.lazy(() => import('../pages/ConfiguracionUsuarios.jsx'));
const PlatformAdmin = React.lazy(() => import('../pages/PlatformAdmin.jsx'));

// App móvil de carga en obra: se baja recién al entrar a /app, para no sumarle
// peso al arranque de la app web, que es la que usa la mayoría.
const AppMovilLayout = React.lazy(() => import('../pages/AppMovil/AppMovilLayout.jsx'));
const InicioMovil = React.lazy(() => import('../pages/AppMovil/InicioMovil.jsx'));
const EscanearEquipo = React.lazy(() => import('../pages/AppMovil/EscanearEquipo.jsx'));
const EquipoMovil = React.lazy(() => import('../pages/AppMovil/EquipoMovil.jsx'));
const BandejaEnvios = React.lazy(() => import('../pages/AppMovil/BandejaEnvios.jsx'));
const CorregirEnvio = React.lazy(() => import('../pages/AppMovil/CorregirEnvio.jsx'));
// Instalación de la app: la landing pública que se le pasa al operario, y la
// pantalla desde la que el administrador se la entrega (QR + dirección).
const InstalarApp = React.lazy(() => import('../pages/AppMovil/InstalarApp.jsx'));
const CompartirApp = React.lazy(() => import('../pages/AppMovil/CompartirApp.jsx'));
const ParteDiarioMovil = React.lazy(() => import('../pages/AppMovil/ParteDiarioMovil.jsx'));
const LubricantesMovil = React.lazy(() => import('../pages/AppMovil/LubricantesMovil.jsx'));
const CombustibleMovil = React.lazy(() => import('../pages/AppMovil/CombustibleMovil.jsx'));


/**
 * Configuración de rutas de la aplicación
 * Usando el nuevo Dashboard.jsx y Layout.jsx que replican exactamente el HTML original
 */
const AppRoutes = () => {
  const { isAuthenticated, user, debeCambiarPassword } = useAuth();

  // Gate de seguridad: si el usuario autenticado debe cambiar su contraseña
  // inicial (admin creado con credencial por defecto), no puede acceder a nada
  // hasta hacerlo. Solo cambiar la contraseña o cerrar sesión. Va antes de
  // cualquier ruta para que no haya forma de saltearlo por URL.
  if (isAuthenticated && debeCambiarPassword) {
    return <CambioPasswordObligatorio />;
  }

  const platformStandalone = isPlatformStandaloneMode();

  // Destino post-login según el rol con portal aislado. Quién queda encerrado en
  // qué portal lo define utils/portales.js, y lo aplica ProtectedRoute para
  // todas las rutas de una: acá solo se usa para saber a dónde mandarlo.
  const destinoPortal = isAuthenticated ? inicioDePortal(user?.rol) : '/';

  // Debug temporal: verificar tipos de algunos componentes importados
  if (process.env.NODE_ENV === 'development') {
    // Evitar spamear: loguear solo una vez
    if (!window.__ROUTES_DEBUGGED__) {
      window.__ROUTES_DEBUGGED__ = true;
      // Mostrar tipo y claves si es objeto
      const inspect = (label, val) => {
        if (typeof val === 'function') {
          console.log(`[AppRoutes][OK] ${label} es function`);
        } else if (React.isValidElement(val)) {
          console.log(`[AppRoutes][OK] ${label} es elemento React`);
        } else {
          console.warn(`[AppRoutes][WARN] ${label} no es función. typeof=`, typeof val, 'keys=', val && Object.keys(val));
        }
      };
      // Sólo componentes de carga estática: los lazy son objetos, no funciones,
      // y dispararían un WARN que no significa nada.
      const debugMap = {
        Layout,
        ProtectedRoute,
        Login,
        Dashboard,
        ListaEquipos,
      };
      Object.entries(debugMap).forEach(([name, ref]) => {
        try { inspect(name, ref); } catch {}
      });
    }
  }

  if (platformStandalone) {
    // La consola de plataforma también necesita su Suspense: PlatformAdmin se
    // carga bajo demanda como el resto de las pantallas fuera del arranque.
    return (
      <React.Suspense fallback={null}>
        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
          />
          <Route
            path="/"
            element={
              <ProtectedRoute rolesPermitidos={['admin']}>
                <PlatformAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />}
          />
        </Routes>
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={null}>
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated
          ? <Navigate to={destinoPortal} replace />
          : <Login />}
      />

      {/* Aterrizaje del login unificado desde la landing: canjea el código
          de un solo uso por el JWT y entra directo. Público (aún sin sesión). */}
      <Route path="/acceso" element={<AccesoHandoff />} />

      {/* Instalación de la app móvil — pública a propósito: es la dirección que
          se le pasa al operario, y pedirle usuario y contraseña para instalar
          algo que después va a usar sin login no filtra nada, sólo agrega un
          paso donde se abandona. */}
      <Route path="/movil" element={<InstalarApp />} />

      {/* Portal QR — acceso público, sin login requerido */}
      <Route path="/qr/:equipoId" element={<PortalQRPage />} />
      <Route path="/qr/:equipoId/parte-diario" element={<PortalQRParteDiario />} />
      <Route path="/qr/:equipoId/documentos" element={<PortalQRDocumentos />} />
      {/* Reparaciones QR — requiere login pero accesible para todos los roles */}
      <Route path="/qr/:equipoId/reparaciones" element={<PortalQRReparaciones />} />
      {/* CheckList desde QR — sin Layout (sidebar/banner), para cualquier usuario autenticado */}
      <Route path="/qr/:equipoId/checklist/nuevo" element={
        <ProtectedRoute>
          <CheckListFormulario modoQR />
        </ProtectedRoute>
      } />
      <Route path="/qr/:equipoId/checklist/:checklistId" element={
        <ProtectedRoute>
          <CheckListFormulario modoQR />
        </ProtectedRoute>
      } />

      {/* App móvil de carga en obra (PWA instalable). Funciona sin conexión:
          los catálogos viven en el dispositivo y lo que se carga sale de una
          cola cuando vuelve la señal.

          **Sin login**, igual que el portal QR. Los partes diarios los cargan
          maquinistas y choferes, que rotan constantemente: exigirles cuenta
          significaría dar de alta —y facturar— cientos de usuarios para algo
          que ya es público desde el QR pegado en la máquina. El bundle de
          catálogos se auto-limita al alcance anónimo (ver
          `apps/taller/sync_offline.py`).

          Lubricantes y combustible sí piden sesión: son de usuarios
          habilitados. El gate está por pantalla y no acá arriba, para que un
          anónimo llegue al inicio y al parte diario sin rebotar al login. */}
      <Route path="/app" element={<AppMovilLayout />}>
        <Route index element={<InicioMovil />} />
        <Route path="escanear" element={<EscanearEquipo />} />
        <Route path="parte-diario" element={<ParteDiarioMovil />} />
        {/* La ficha va antes que su sub-ruta para que quede claro el orden de
            lectura; React Router matchea la más específica igual. */}
        <Route path="equipo/:equipoId" element={<EquipoMovil />} />
        <Route path="equipo/:equipoId/parte-diario" element={<ParteDiarioMovil />} />
        <Route
          path="lubricantes"
          element={<ProtectedRoute><LubricantesMovil /></ProtectedRoute>}
        />
        <Route
          path="combustible"
          element={<ProtectedRoute><CombustibleMovil /></ProtectedRoute>}
        />
        <Route path="pendientes" element={<BandejaEnvios />} />
        <Route path="pendientes/:envioId" element={<CorregirEnvio />} />
      </Route>

      {/* Portal exclusivo para mecánicos */}
      <Route
        path="/portal-mecanico"
        element={
          <ProtectedRoute rolesPermitidos={['mecanico']}>
            <PortalMecanicoDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-mecanico/otc/nueva"
        element={
          <ProtectedRoute rolesPermitidos={['mecanico']}>
            <PaginaOTC modoMecanico />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-mecanico/otc/:id"
        element={
          <ProtectedRoute rolesPermitidos={['mecanico']}>
            <PaginaOTC modoMecanico />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-mecanico/otp/:id"
        element={
          <ProtectedRoute rolesPermitidos={['mecanico']}>
            <PlanillaMantenimiento modoMecanico />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-mecanico/checklist/:equipoId/nuevo"
        element={
          <ProtectedRoute rolesPermitidos={['mecanico']}>
            <CheckListFormulario modoMecanico />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal-mecanico/checklist/:equipoId/:checklistId"
        element={
          <ProtectedRoute rolesPermitidos={['mecanico']}>
            <CheckListFormulario modoMecanico />
          </ProtectedRoute>
        }
      />

      {/* Portal exclusivo para despachantes de combustible */}
      <Route
        path="/portal-despacho"
        element={
          <ProtectedRoute rolesPermitidos={['despachante']}>
            <PortalDespachoDashboard />
          </ProtectedRoute>
        }
      />

      {/* Sistema completo. ProtectedRoute manda a su portal a los roles que
          viven encerrados en uno, así que alcanza con envolver el padre: ningún
          hijo puede quedar expuesto por olvido, ni siquiera uno agregado
          mañana. */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Inicio: Dashboard */}
        <Route index element={<Dashboard />} />

        {/* Equipos */}
        <Route path="equipos" element={<Navigate to="/flota/grilla" replace />} />
        <Route path="equipos/crear" element={<FormularioEquipo fullWidth={true} />} />
        <Route path="equipos/:id" element={<PerfilEquipo />} />
        <Route path="equipos/:equipoId/checklist/nuevo" element={<CheckListFormulario />} />
        <Route path="equipos/:equipoId/checklist/:checklistId" element={<CheckListFormulario />} />
        <Route path="checklist/:checklistId/imprimir" element={<CheckListImpresion />} />
        <Route path="checklist/plantilla/:plantillaId/imprimir" element={<CheckListImpresion />} />
        <Route path="equipos/:id/clonar" element={<FormularioEquipo fullWidth={true} />} />
        <Route path="equipos/:id/editar" element={<FormularioEquipo fullWidth={true} />} />
        <Route path="flota/grilla" element={<FlotaGrilla />} />

        {/* Operación */}
        <Route path="reparaciones" element={<ListaReparaciones />} />
        <Route path="reparaciones/otc/nueva" element={<PaginaOTC />} />
        <Route path="reparaciones/otc/:id" element={<PaginaOTC />} />
        <Route path="almacen" element={<AlmacenPage />} />
        {/* Logística */}
        <Route path="logistica" element={<ListaLogistica />} />
        <Route path="logistica/nuevo" element={<PaginaLogistica />} />
        <Route path="logistica/:id" element={<PaginaLogistica />} />
        <Route path="rental" element={<ListaRental />} />
        <Route path="rental/clientes" element={<ConfiguracionClientesAlquiler />} />
        <Route path="rental/nuevo" element={<PaginaRental />} />
        <Route path="rental/:id" element={<PaginaRental />} />
        {/* Informes */}
        <Route
          path="informes"
          element={(
            <React.Suspense fallback={null}>
              <InformesPage />
            </React.Suspense>
          )}
        />
        <Route path="partes" element={<ListaPartesDiarios />} />
        <Route path="lubricantes" element={<ListaLubricantes />} />
        <Route path="documentacion" element={<ListaDocumentacion />} />
  {/* Mantenimiento */}
  <Route path="mantenimientos" element={<ListaMantenimientos />} />
  {/* Nueva vista de Planes, más parecida al legacy */}
  <Route path="planes-mantenimiento" element={<PlanesMantenimiento />} />
  <Route path="planes-mantenimiento/nuevo" element={<PlanForm />} />
  <Route path="planes-mantenimiento/:id/editar" element={<PlanForm />} />
  <Route path="planes-mantenimiento/:id/clonar" element={<PlanForm />} />
  <Route path="planes-mantenimiento/:planId/grilla" element={<GrillaPlanMantenimiento />} />
  <Route path="planes-mantenimiento/:planId/rutina/nueva" element={<RutinaMantenimiento />} />
  <Route path="rutinas-mantenimiento" element={<RutinasMantenimiento />} />
  <Route path="rutinas-mantenimiento/:rutinaId/editar" element={<RutinaMantenimiento />} />
  <Route path="tareas-mantenimiento" element={<TareasMantenimiento />} />
  <Route path="mantenimientos/:id/planilla" element={<PlanillaMantenimiento />} />

        {/* Changelog / versiones */}
        <Route path="changelog" element={<Changelog />} />

        {/* Impresión QR */}
        <Route path="qr-imprimir" element={<QrImprimir />} />

        {/* Configuración - Página índice */}
        <Route path="configuracion" element={<ConfiguracionPage />} />

        {/* Configuración - Módulos */}
        <Route path="configuracion/marcas" element={<ConfiguracionMarcas />} />
        <Route path="configuracion/modelos" element={<ConfiguracionModelos />} />
        <Route path="configuracion/familias" element={<ConfiguracionFamilias />} />
        <Route path="configuracion/tipos-equipo" element={<ConfiguracionTiposEquipo />} />
        <Route path="configuracion/estados-equipo" element={<ConfiguracionEstadosEquipo />} />
        <Route path="configuracion/sucursales" element={<ConfiguracionSucursales />} />
        <Route path="configuracion/centros-costo" element={<ConfiguracionCentrosCosto />} />

        <Route path="configuracion/mecanicos" element={<ConfiguracionMecanicos />} />
        <Route path="configuracion/motivos-detencion" element={<ConfiguracionMotivosDetencion />} />
        <Route path="configuracion/estados-reparacion" element={<ConfiguracionEstadosReparacion />} />
        <Route path="configuracion/tipos-contador" element={<ConfiguracionTiposContador />} />
        <Route path="configuracion/tareas-engrase" element={<ConfiguracionTareasEngrase />} />
        <Route path="configuracion/tipos-documentacion" element={<ConfiguracionTiposDocumentacion />} />
        <Route path="configuracion/obras" element={<ConfiguracionObras />} />
        <Route path="configuracion/sectores-trabajo" element={<ConfiguracionSectoresTrabajo />} />
        <Route path="configuracion/sistemas" element={<ConfiguracionSistemas />} />
        <Route path="configuracion/componentes" element={<ConfiguracionComponentes />} />
        <Route path="configuracion/depositos" element={<ConfiguracionDepositos />} />
        <Route path="configuracion/tareas" element={<ConfiguracionTareas />} />
        <Route path="configuracion/tipos-rutina" element={<ConfiguracionTiposRutina />} />
        <Route path="configuracion/familias-repuesto" element={<ConfiguracionFamiliasRepuesto />} />
        <Route path="configuracion/subfamilias-repuesto" element={<ConfiguracionSubfamiliasRepuesto />} />
        <Route path="configuracion/marcas-repuesto" element={<ConfiguracionMarcasRepuesto />} />
        <Route path="configuracion/unidades-medida" element={<ConfiguracionUnidadesMedida />} />
        <Route path="configuracion/repuestos" element={<ConfiguracionRepuestos />} />
  <Route path="configuracion/empresa" element={<ConfiguracionEmpresa />} />
        <Route path="configuracion/empresas" element={<ConfiguracionEmpresas />} />
        <Route path="configuracion/checklist" element={<ConfiguracionCheckList />} />
        <Route path="configuracion/puntos-control" element={<ConfiguracionPuntosControl />} />
        {/* Logística */}
        <Route path="configuracion/categorias-logistica" element={<ConfiguracionCategoriasLogistica />} />
        <Route path="configuracion/categorias-cargas" element={<ConfiguracionCategoriasCargas />} />
        <Route path="configuracion/centros-logisticos" element={<ConfiguracionCentrosLogisticos />} />
        <Route path="configuracion/transportistas" element={<ConfiguracionTransportistas />} />
        <Route path="configuracion/proveedores" element={<ConfiguracionProveedores />} />
        <Route path="configuracion/ubicaciones" element={<ConfiguracionUbicacionesLogisticas />} />
        <Route path="configuracion/flota-transporte" element={<ConfiguracionFlotaTransporte />} />
        {/* GPS */}
        <Route path="configuracion/conectores-gps" element={<ConfiguracionConectoresGPS />} />
        <Route path="configuracion/mapeo-gps" element={<ConfiguracionMapeoGPS />} />
        {/* Despacho de combustible */}
        <Route path="combustible" element={<ListaDespachosCombustible />} />
        <Route path="configuracion/lugares-despacho" element={<ConfiguracionLugaresDespacho />} />
        <Route path="configuracion/tipos-combustible" element={<ConfiguracionTiposCombustible />} />
        <Route path="configuracion/app-celular" element={<CompartirApp />} />
              {/* Gestión de usuarios: solo accesible para administradores */}
              <Route
                path="configuracion/usuarios"
                element={
                  <ProtectedRoute rolesPermitidos={['admin']}>
                    <ConfiguracionUsuarios />
                  </ProtectedRoute>
                }
              />
      </Route>
      <Route path="*" element={<Navigate to={destinoPortal} replace />} />
    </Routes>
    </React.Suspense>
  );
};

export default AppRoutes;
